import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Badge, Button, Card, Col, Form, Nav, ProgressBar, Row, Table } from "react-bootstrap";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import axiosInstance from "../services/axiosInterceptor";
import { useLoading } from "../contexts/LoadingContext";
import ThaiDateInput from "../components/ThaiDateInput";
import { toISODate, toThaiDate } from "../utils/thaiDate";
import MissingGradesModal from "../reports/MissingGradesModal";

const API = process.env.REACT_APP_API;

// หน้าภาพรวมของหัวหน้ากลุ่มสาระ (pos 30) — อ่านอย่างเดียว
// ข้อมูลทุกแถวคือหนึ่งการสอน (วิชา × ห้อง) จาก /subject-head/overview ซึ่งใช้นิยามเดียวกับ
// รายงานการส่งเกรดของฝ่ายวิชาการ ส่วนแท็บเช็คชื่อใช้ตัวคำนวณเดียวกับรายงานการเข้าสอนของครู

const GRADE_LABELS = ['4', '3.5', '3', '2.5', '2', '1.5', '1', '0'];
const GRADE_POINT = { '4': 4, '3.5': 3.5, '3': 3, '2.5': 2.5, '2': 2, '1.5': 1.5, '1': 1, '0': 0 };
const gradeTextClass = (label) => {
  const p = GRADE_POINT[label];
  if (p >= 3) return 'text-success';
  if (p >= 2) return 'text-primary';
  if (p >= 1) return 'text-warning';
  return 'text-danger';
};

const STATUS_META = {
  none:     { label: 'ยังไม่เริ่ม', bg: 'danger' },
  partial:  { label: 'ส่งไม่ครบ',  bg: 'warning' },
  complete: { label: 'ครบ',        bg: 'success' },
};

// สถานะช่องคะแนนเก็บของหนึ่งการสอน
const ITEM_META = {
  no_items: { label: 'ยังไม่ตั้งช่องคะแนน', bg: 'danger' },
  not_full: { label: 'คะแนนเต็มไม่ครบ 100', bg: 'warning' },
  no_marks: { label: 'ตั้งแล้ว ยังไม่กรอก', bg: 'secondary' },
  filling:  { label: 'กำลังกรอก',           bg: 'info' },
  done:     { label: 'กรอกครบ',             bg: 'success' },
};
const ITEM_ORDER = ['no_items', 'not_full', 'no_marks', 'filling', 'done'];

const itemStateOf = (r) => {
  if (!r.item_count) return 'no_items';
  if (Math.round(Number(r.item_max_total)) !== 100) return 'not_full';
  const slots = r.item_count * r.total_students;
  if (!r.marks_filled) return 'no_marks';
  return r.marks_filled >= slots ? 'done' : 'filling';
};
const fillPct = (r) => {
  const slots = r.item_count * r.total_students;
  return slots ? Math.round((r.marks_filled / slots) * 100) : 0;
};

const roomLabel = (r) => `ม.${r.class_room}`;
const pct = (n, d) => (d ? Math.round((n / d) * 1000) / 10 : 0);
const fmtAvg = (v) => (v == null ? '-' : Number(v).toFixed(2));

// รวมการกระจายเกรดหลายแถว
const sumDist = (rows) => {
  const dist = Object.fromEntries(GRADE_LABELS.map(l => [l, 0]));
  let graded = 0, waiting = 0, total = 0, sumPoint = 0;
  rows.forEach(r => {
    total += r.total_students;
    waiting += r.waiting_count;
    if (!r.grade_dist) return;
    GRADE_LABELS.forEach(l => {
      const n = Number(r.grade_dist[l] || 0);
      dist[l] += n; graded += n; sumPoint += GRADE_POINT[l] * n;
    });
  });
  return { dist, graded, waiting, total, avg: graded ? sumPoint / graded : null };
};

// ภาคเรียนปัจจุบันตามปฏิทินโรงเรียน (พ.ค.–ต.ค. = ภาค 1) — กติกาเดียวกับ getCurrentSemester
const currentSemester = () => {
  const m = new Date().getMonth() + 1;
  return m >= 5 && m <= 10 ? '1' : '2';
};

const readPref = (key, fallback) => {
  try { return localStorage.getItem(key) || fallback; } catch { return fallback; }
};
const writePref = (key, value) => {
  try { localStorage.setItem(key, value); } catch { /* ไม่มีที่เก็บก็ใช้ค่าตั้งต้น */ }
};

const firstOfMonth = () => {
  const d = new Date();
  return toISODate(new Date(d.getFullYear(), d.getMonth(), 1));
};

export default function SubjectHeadDashboard() {
  const { setLoading } = useLoading();
  const [skin, setSkin] = useState(localStorage.getItem('skin-mode') ? 'dark' : '');

  const [yearList, setYearList] = useState([]);
  const [yearId, setYearId]     = useState(null);
  const [semester, setSemester] = useState(currentSemester);
  const [dept, setDept]         = useState(() => readPref('subjectHead_dept', 'all'));
  const [tab, setTab]           = useState('overview');
  const [search, setSearch]     = useState('');

  const [groups, setGroups] = useState([]);
  const [rows, setRows]     = useState([]);
  const [loadError, setLoadError] = useState(false);
  const [openTeachers, setOpenTeachers] = useState(() => new Set());
  const [openSubjects, setOpenSubjects] = useState(() => new Set());
  const [itemFilter, setItemFilter] = useState(['no_items', 'not_full', 'no_marks', 'filling']);
  const [missingRow, setMissingRow] = useState(null);

  const [checkStart, setCheckStart] = useState(firstOfMonth);
  const [checkEnd, setCheckEnd]     = useState(() => toISODate(new Date()));
  const [check, setCheck]           = useState(null);
  const [checkError, setCheckError] = useState('');

  useEffect(() => {
    const tables = document.getElementsByTagName('table');
    for (const t of tables) {
      const theads = t.querySelectorAll('thead');
      if (skin === 'dark') {
        for (const th of theads) { th.classList.remove('table-secondary'); th.classList.add('table-primary'); }
        t.classList.add('table-dark');
      } else {
        t.classList.remove('table-dark');
        for (const th of theads) { th.classList.remove('table-primary'); th.classList.add('table-secondary'); }
      }
    }
  });

  useEffect(() => { writePref('subjectHead_dept', dept); }, [dept]);

  useEffect(() => {
    axiosInstance.get(`${API}/getStudyYear`)
      .then(res => {
        const years = res.data || [];
        setYearList(years);
        if (years.length > 0) setYearId(years[0].year_id);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!yearId) return;
    setLoading(true);
    setLoadError(false);
    const params = new URLSearchParams({ year_id: yearId, dept });
    if (semester !== 'all') params.set('semester', semester);
    axiosInstance.get(`${API}/subject-head/overview?${params}`)
      .then(res => {
        setGroups(res.data?.groups || []);
        setRows(res.data?.rows || []);
        setOpenTeachers(new Set());
        setOpenSubjects(new Set());
      })
      .catch(() => { setRows([]); setLoadError(true); })
      .finally(() => setLoading(false));
  }, [yearId, semester, dept]); // eslint-disable-line react-hooks/exhaustive-deps

  // แท็บเช็คชื่อโหลดเมื่อเปิดแท็บเท่านั้น — คิวรีขยายตารางสอนลงทุกวันในช่วง ใช้เวลาหลายวินาที
  useEffect(() => {
    if (tab !== 'check' || !yearId || !checkStart || !checkEnd) return;
    if (checkEnd < checkStart) { setCheckError('วันที่สิ้นสุดต้องไม่ก่อนวันที่เริ่ม'); setCheck(null); return; }
    setLoading(true);
    setCheckError('');
    const params = new URLSearchParams({ year_id: yearId, dept, start: checkStart, end: checkEnd });
    axiosInstance.get(`${API}/subject-head/teaching-check?${params}`)
      .then(res => setCheck(res.data))
      .catch(err => { setCheck(null); setCheckError(err.response?.data?.message || 'โหลดข้อมูลไม่สำเร็จ'); })
      .finally(() => setLoading(false));
  }, [tab, yearId, dept, checkStart, checkEnd]); // eslint-disable-line react-hooks/exhaustive-deps

  const q = search.trim().toLowerCase();
  const matches = (r) => !q
    || String(r.t_name || '').toLowerCase().includes(q)
    || String(r.sub_subid || '').toLowerCase().includes(q)
    || String(r.sub_subname || '').toLowerCase().includes(q);
  const visible = useMemo(() => rows.filter(matches), [rows, q]); // eslint-disable-line react-hooks/exhaustive-deps

  const deptLabel = dept === 'all' ? 'ทุกกลุ่มสาระ' : (groups.find(g => g.key === dept)?.label || '');

  /* ── ภาพรวม ─────────────────────────────────────────────── */
  const kpi = useMemo(() => {
    const teachers = new Set(visible.map(r => r.t_id).filter(Boolean));
    const complete = visible.filter(r => r.status === 'complete').length;
    const notStarted = visible.filter(r => r.status === 'none').length;
    const noItems = visible.filter(r => !r.item_count).length;
    const d = sumDist(visible);
    return { teachers: teachers.size, total: visible.length, complete, notStarted, noItems, avg: d.avg, graded: d.graded, students: d.total };
  }, [visible]);

  const teacherRows = useMemo(() => {
    const map = new Map();
    visible.forEach(r => {
      const key = r.t_id ?? 'none';
      if (!map.has(key)) map.set(key, { t_id: key, t_name: r.t_name || '(ไม่ระบุครู)', rows: [], subjects: new Set() });
      const t = map.get(key);
      t.rows.push(r);
      t.subjects.add(`${r.sub_id}-${r.sub_semester}`);
    });
    return [...map.values()].map(t => {
      const none = t.rows.filter(r => r.status === 'none').length;
      const partial = t.rows.filter(r => r.status === 'partial').length;
      const withItems = t.rows.filter(r => r.item_count > 0).length;
      const slots = t.rows.reduce((s, r) => s + r.item_count * r.total_students, 0);
      const filled = t.rows.reduce((s, r) => s + r.marks_filled, 0);
      const d = sumDist(t.rows);
      return {
        ...t, subjectCount: t.subjects.size, none, partial, complete: t.rows.length - none - partial,
        withItems, fill: slots ? Math.round((filled / slots) * 100) : null, avg: d.avg,
        rows: [...t.rows].sort((a, b) => String(a.sub_subid).localeCompare(String(b.sub_subid)) || (a.tb_class_c_id - b.tb_class_c_id) || (a.r_no - b.r_no)),
      };
    }).sort((a, b) => (b.none - a.none) || ((b.rows.length - b.withItems) - (a.rows.length - a.withItems)) || (b.partial - a.partial)
      || a.t_name.localeCompare(b.t_name, 'th'));
  }, [visible]);

  /* ── ผลสัมฤทธิ์ ─────────────────────────────────────────── */
  const subjectRows = useMemo(() => {
    const map = new Map();
    visible.forEach(r => {
      const key = `${r.sub_id}-${r.sub_semester}`;
      if (!map.has(key)) map.set(key, { key, sub_subid: r.sub_subid, sub_subname: r.sub_subname, sub_type: r.sub_type, sub_semester: r.sub_semester, rows: [], teachers: new Set() });
      map.get(key).rows.push(r);
      if (r.t_name) map.get(key).teachers.add(r.t_name);
    });
    return [...map.values()].map(s => ({
      ...s,
      ...sumDist(s.rows),
      rows: [...s.rows].sort((a, b) => (a.tb_class_c_id - b.tb_class_c_id) || (a.r_no - b.r_no)),
    })).sort((a, b) => String(a.sub_semester).localeCompare(String(b.sub_semester)) || String(a.sub_subid).localeCompare(String(b.sub_subid)));
  }, [visible]);
  const groupDist = useMemo(() => sumDist(visible), [visible]);

  /* ── ช่องคะแนนเก็บ ──────────────────────────────────────── */
  const itemCounts = useMemo(() => {
    const c = Object.fromEntries(ITEM_ORDER.map(k => [k, 0]));
    visible.forEach(r => { c[itemStateOf(r)] += 1; });
    return c;
  }, [visible]);
  const itemRows = useMemo(() => visible
    .filter(r => itemFilter.includes(itemStateOf(r)))
    .sort((a, b) => (ITEM_ORDER.indexOf(itemStateOf(a)) - ITEM_ORDER.indexOf(itemStateOf(b))) || (fillPct(a) - fillPct(b))
      || String(a.t_name || '').localeCompare(String(b.t_name || ''), 'th')),
  [visible, itemFilter]);

  const toggleSet = (setter) => (id) => setter(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const toggleTeacher = toggleSet(setOpenTeachers);
  const toggleSubject = toggleSet(setOpenSubjects);
  const toggleItemFilter = (k) => setItemFilter(prev => prev.includes(k) ? prev.filter(x => x !== k) : [...prev, k]);

  const checkTeachers = useMemo(() => (check?.teachers || []).filter(t => !q || String(t.t_name || '').toLowerCase().includes(q)), [check, q]);
  const checkMissed = useMemo(() => (check?.missed || []).filter(m => !q
    || String(m.t_name || '').toLowerCase().includes(q) || String(m.subject || '').toLowerCase().includes(q)), [check, q]);

  const distCells = (dist, graded) => GRADE_LABELS.map(l => (
    <td key={l} className={`text-center ${dist[l] ? gradeTextClass(l) : 'text-muted'}`}>
      {dist[l] || '-'}
      {dist[l] > 0 && graded > 0 && <div className="fs-xs text-muted">{pct(dist[l], graded)}%</div>}
    </td>
  ));

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">

        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="#">หัวหน้ากลุ่มสาระ</Link></li>
              <li className="breadcrumb-item active">ภาพรวมกลุ่มสาระ</li>
            </ol>
            <h4 className="main-title mb-0">ภาพรวมกลุ่มสาระ{deptLabel && dept !== 'all' ? ` · ${deptLabel}` : ''}</h4>
          </div>
          <div className="d-flex flex-wrap align-items-center gap-2">
            <select className="form-select form-select-sm" style={{ minWidth: 150 }}
              value={yearId || ''} onChange={e => setYearId(e.target.value ? Number(e.target.value) : null)}>
              {yearList.map(y => <option key={y.year_id} value={y.year_id}>ปีการศึกษา {y.year_year}</option>)}
            </select>
            <div className="btn-group btn-group-sm">
              {[['1', 'ภาคเรียน 1'], ['2', 'ภาคเรียน 2'], ['all', 'ทั้งปี']].map(([v, label]) => (
                <button key={v} className={`btn btn${semester === v ? '' : '-outline'}-primary`} onClick={() => setSemester(v)}>{label}</button>
              ))}
            </div>
          </div>
        </div>

        {/* เลือกกลุ่มสาระ — ตัวเลขแดง = วิชา × ห้องที่ยังส่งเกรดไม่ครบ */}
        <div className="d-flex flex-wrap gap-1 mb-3">
          {[{ key: 'all', label: 'ทุกกลุ่มสาระ', pending: groups.reduce((s, g) => s + g.pending, 0), total: 1 }, ...groups].map(g => {
            const on = dept === g.key;
            return (
              <Button key={g.key} size="sm" variant={on ? 'primary' : 'outline-primary'}
                disabled={g.key !== 'all' && g.total === 0}
                onClick={() => setDept(g.key)} className="d-inline-flex align-items-center gap-1">
                {g.label}
                {g.pending > 0 && <Badge bg={on ? 'light' : 'danger'} text={on ? 'danger' : undefined} pill>{g.pending}</Badge>}
              </Button>
            );
          })}
        </div>

        <Card className="card-one">
          <Card.Header className="d-flex flex-wrap align-items-center gap-2">
            <Nav variant="pills" activeKey={tab} onSelect={k => setTab(k)} className="flex-wrap">
              <Nav.Item><Nav.Link eventKey="overview" className="py-1 px-3">ภาพรวม</Nav.Link></Nav.Item>
              <Nav.Item><Nav.Link eventKey="achievement" className="py-1 px-3">ผลสัมฤทธิ์รายวิชา</Nav.Link></Nav.Item>
              <Nav.Item><Nav.Link eventKey="items" className="py-1 px-3">ช่องคะแนนเก็บ</Nav.Link></Nav.Item>
              <Nav.Item><Nav.Link eventKey="check" className="py-1 px-3">เช็คชื่อรายคาบ</Nav.Link></Nav.Item>
            </Nav>
            <Form.Control size="sm" className="ms-auto" style={{ maxWidth: 240 }}
              placeholder="ค้นหาครู / วิชา" value={search} onChange={e => setSearch(e.target.value)} />
          </Card.Header>

          {loadError && tab !== 'check' && (
            <Card.Body><div className="text-center text-danger py-4">โหลดข้อมูลไม่สำเร็จ</div></Card.Body>
          )}

          {/* ── ภาพรวม ── */}
          {tab === 'overview' && !loadError && (
            <Card.Body className="p-3">
              <Row className="g-2 mb-3">
                {[
                  { label: 'ครูในกลุ่มสาระ', value: kpi.teachers, unit: 'คน', color: 'primary' },
                  { label: 'วิชา × ห้อง', value: kpi.total, unit: 'รายการ', color: 'secondary' },
                  { label: 'ส่งเกรดครบ', value: `${kpi.complete}/${kpi.total}`, unit: `${pct(kpi.complete, kpi.total)}%`, color: 'success' },
                  { label: 'ยังไม่เริ่มป้อนเกรด', value: kpi.notStarted, unit: 'ห้อง', color: 'danger' },
                  { label: 'ยังไม่ตั้งช่องคะแนน', value: kpi.noItems, unit: 'ห้อง', color: 'warning' },
                  { label: 'เกรดเฉลี่ย', value: fmtAvg(kpi.avg), unit: `จาก ${kpi.graded}/${kpi.students} คนที่มีเกรด`, color: 'info' },
                ].map(k => (
                  <Col key={k.label} xs={6} md={4} xl={2}>
                    <div className={`border rounded p-2 h-100 border-start border-4 border-${k.color}`}>
                      <div className="fs-xs text-muted">{k.label}</div>
                      <div className={`fs-4 fw-bold text-${k.color}`}>{k.value}</div>
                      <div className="fs-xs text-muted">{k.unit}</div>
                    </div>
                  </Col>
                ))}
              </Row>

              <div className="d-flex align-items-center mb-2">
                <span className="fs-sm text-muted">ครู {teacherRows.length} คน · เรียงคนที่ต้องติดตามขึ้นก่อน · กดแถวเพื่อดูรายห้อง</span>
                <Link to="/academic/grade-submission-report" className="fs-sm ms-auto">รายงานการส่งเกรดแบบละเอียด</Link>
              </div>
              {teacherRows.length === 0 ? (
                <div className="text-center text-muted py-4">ไม่มีข้อมูล</div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <Table hover size="sm" className="mb-0 align-middle" style={{ minWidth: 760 }}>
                    <thead className="table-secondary">
                      <tr>
                        <th>ครูผู้สอน</th>
                        <th className="text-center">วิชา</th>
                        <th className="text-center">ห้อง</th>
                        <th className="text-center">ส่งเกรด</th>
                        <th className="text-center">ตั้งช่องคะแนน</th>
                        <th style={{ width: 140 }}>กรอกคะแนนเก็บ</th>
                        <th className="text-center">เกรดเฉลี่ย</th>
                      </tr>
                    </thead>
                    <tbody>
                      {teacherRows.map(t => (
                        <React.Fragment key={t.t_id}>
                          <tr role="button" onClick={() => toggleTeacher(t.t_id)}>
                            <td className="fs-sm"><i className={`ri-arrow-${openTeachers.has(t.t_id) ? 'down' : 'right'}-s-line me-1`} />{t.t_name}</td>
                            <td className="text-center">{t.subjectCount}</td>
                            <td className="text-center">{t.rows.length}</td>
                            <td className="text-center text-nowrap">
                              {t.none > 0 && <Badge bg="danger" className="me-1">ยังไม่เริ่ม {t.none}</Badge>}
                              {t.partial > 0 && <Badge bg="warning" text="dark" className="me-1">ไม่ครบ {t.partial}</Badge>}
                              {t.complete > 0 && <Badge bg="success">ครบ {t.complete}</Badge>}
                            </td>
                            <td className={`text-center ${t.withItems < t.rows.length ? 'text-danger fw-semibold' : ''}`}>{t.withItems}/{t.rows.length}</td>
                            <td>{t.fill == null ? <span className="text-muted fs-sm">-</span> : (
                              <div className="d-flex align-items-center gap-2">
                                <ProgressBar now={t.fill} style={{ height: 6, flex: 1 }} variant={t.fill >= 100 ? 'success' : 'primary'} />
                                <span className="fs-xs">{t.fill}%</span>
                              </div>
                            )}</td>
                            <td className="text-center">{fmtAvg(t.avg)}</td>
                          </tr>
                          {openTeachers.has(t.t_id) && t.rows.map(r => {
                            const st = STATUS_META[r.status];
                            const it = ITEM_META[itemStateOf(r)];
                            return (
                              <tr key={r.teach_id} className="fs-sm">
                                <td className="ps-4"><span className="text-muted">{r.sub_subid}</span> {r.sub_subname} <span className="text-muted">· ภาค {r.sub_semester}</span></td>
                                <td />
                                <td className="text-center text-nowrap">{roomLabel(r)}</td>
                                <td className="text-center text-nowrap">
                                  <Badge bg={st.bg} text={r.status === 'partial' ? 'dark' : undefined}>{st.label}</Badge>{' '}
                                  <span className="fs-xs">{r.graded_count}/{r.total_students}</span>
                                  {r.status !== 'complete' && (
                                    <Button size="sm" variant="link" className="p-0 ms-1 fs-xs" onClick={() => setMissingRow(r)}>รายชื่อ</Button>
                                  )}
                                </td>
                                <td className="text-center"><Badge bg={it.bg} className="fs-xs">{r.item_count ? `${r.item_count} ช่อง` : it.label}</Badge></td>
                                <td>{r.item_count ? <span className="fs-xs">{fillPct(r)}%</span> : null}</td>
                                <td className="text-center">{fmtAvg(r.grade_dist ? sumDist([r]).avg : null)}</td>
                              </tr>
                            );
                          })}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          )}

          {/* ── ผลสัมฤทธิ์รายวิชา ── */}
          {tab === 'achievement' && !loadError && (
            <Card.Body className="p-0">
              <div className="px-3 py-2 border-bottom fs-sm text-muted">
                <i className="ri-information-line me-1" />
                นับเฉพาะนักเรียนที่มีเกรดแล้วและไม่ติด ร — ระหว่างภาคคะแนนยังเป็นคะแนนสะสม คอลัมน์ "มีเกรด" บอกว่าข้อมูลครบแค่ไหน · กดแถววิชาเพื่อดูรายห้อง
              </div>
              {subjectRows.length === 0 ? (
                <div className="text-center text-muted py-4">ไม่มีข้อมูล</div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <Table hover size="sm" className="mb-0 align-middle" style={{ minWidth: 900 }}>
                    <thead className="table-secondary">
                      <tr>
                        <th>วิชา</th>
                        <th className="text-center">ห้อง</th>
                        <th className="text-center">มีเกรด</th>
                        {GRADE_LABELS.map(l => <th key={l} className="text-center" style={{ width: 52 }}>{l}</th>)}
                        <th className="text-center">ร</th>
                        <th className="text-center">เฉลี่ย</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="fw-semibold">
                        <td>รวม {deptLabel}</td>
                        <td className="text-center">{visible.length}</td>
                        <td className="text-center text-nowrap">{groupDist.graded}/{groupDist.total}</td>
                        {distCells(groupDist.dist, groupDist.graded)}
                        <td className="text-center">{groupDist.waiting || '-'}</td>
                        <td className="text-center">{fmtAvg(groupDist.avg)}</td>
                      </tr>
                      {subjectRows.map(s => (
                        <React.Fragment key={s.key}>
                          <tr role="button" onClick={() => toggleSubject(s.key)}>
                            <td className="fs-sm">
                              <i className={`ri-arrow-${openSubjects.has(s.key) ? 'down' : 'right'}-s-line me-1`} />
                              <span className="text-muted">{s.sub_subid}</span> {s.sub_subname}
                              {s.sub_type === 'extra' && <span className="text-muted"> (เพิ่มเติม)</span>}
                              <span className="text-muted"> · ภาค {s.sub_semester}</span>
                              <div className="fs-xs text-muted ps-3">{[...s.teachers].join(', ')}</div>
                            </td>
                            <td className="text-center">{s.rows.length}</td>
                            <td className="text-center text-nowrap">{s.graded}/{s.total}</td>
                            {distCells(s.dist, s.graded)}
                            <td className="text-center">{s.waiting || '-'}</td>
                            <td className="text-center fw-semibold">{fmtAvg(s.avg)}</td>
                          </tr>
                          {openSubjects.has(s.key) && s.rows.map(r => {
                            const d = sumDist([r]);
                            return (
                              <tr key={r.teach_id} className="fs-sm">
                                <td className="ps-4">{roomLabel(r)} <span className="text-muted">· {r.t_name}</span></td>
                                <td />
                                <td className="text-center text-nowrap">{d.graded}/{d.total}</td>
                                {distCells(d.dist, d.graded)}
                                <td className="text-center">{d.waiting || '-'}</td>
                                <td className="text-center">{fmtAvg(d.avg)}</td>
                              </tr>
                            );
                          })}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          )}

          {/* ── ช่องคะแนนเก็บ ── */}
          {tab === 'items' && !loadError && (
            <Card.Body className="p-0">
              <div className="d-flex flex-wrap align-items-center gap-3 px-3 py-2 border-bottom">
                {ITEM_ORDER.map(k => (
                  <Form.Check key={k} type="checkbox" id={`item-${k}`} className="mb-0"
                    checked={itemFilter.includes(k)} onChange={() => toggleItemFilter(k)}
                    label={<><Badge bg={ITEM_META[k].bg} className="fs-xs">{ITEM_META[k].label}</Badge> <span className="fs-xs text-muted">{itemCounts[k]}</span></>} />
                ))}
                <span className="fs-sm text-muted ms-auto">{itemRows.length} รายการ</span>
              </div>
              <div className="px-3 py-2 border-bottom fs-xs text-muted">
                "กรอกแล้ว" = ช่องคะแนนที่มีคะแนน ÷ (จำนวนช่อง × นักเรียนในห้อง) ไม่นับนักเรียนที่จำหน่ายแล้วหรือย้ายห้อง
              </div>
              {itemRows.length === 0 ? (
                <div className="text-center text-muted py-4">ไม่มีข้อมูลตามตัวกรอง</div>
              ) : (
                <div style={{ maxHeight: 640, overflowY: 'auto', overflowX: 'auto' }}>
                  <Table hover size="sm" className="mb-0 align-middle" style={{ minWidth: 760 }}>
                    <thead className="table-secondary sticky-top">
                      <tr>
                        <th>ครูผู้สอน</th>
                        <th>วิชา</th>
                        <th className="text-center">ห้อง</th>
                        <th className="text-center">จำนวนช่อง</th>
                        <th className="text-center">เต็มรวม</th>
                        <th style={{ width: 170 }}>กรอกแล้ว</th>
                        <th className="text-center">สถานะ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {itemRows.map(r => {
                        const k = itemStateOf(r);
                        const p = fillPct(r);
                        return (
                          <tr key={r.teach_id}>
                            <td className="fs-sm">{r.t_name}</td>
                            <td className="fs-sm"><span className="text-muted">{r.sub_subid}</span> {r.sub_subname} <span className="text-muted">· ภาค {r.sub_semester}</span></td>
                            <td className="text-center text-nowrap">{roomLabel(r)}</td>
                            <td className="text-center">{r.item_count || '-'}</td>
                            <td className={`text-center ${k === 'not_full' ? 'text-danger fw-semibold' : ''}`}>{r.item_count ? Number(r.item_max_total) : '-'}</td>
                            <td>{r.item_count ? (
                              <div className="d-flex align-items-center gap-2">
                                <ProgressBar now={p} style={{ height: 6, flex: 1 }} variant={p >= 100 ? 'success' : 'primary'} />
                                <span className="fs-xs text-nowrap">{p}%</span>
                              </div>
                            ) : <span className="text-muted fs-sm">-</span>}</td>
                            <td className="text-center"><Badge bg={ITEM_META[k].bg} className="fs-xs">{ITEM_META[k].label}</Badge></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          )}

          {/* ── เช็คชื่อรายคาบ ── */}
          {tab === 'check' && (
            <Card.Body className="p-3">
              <Row className="g-2 align-items-end mb-3">
                <Col xs="auto">
                  <label className="form-label fs-sm mb-1">ตั้งแต่วันที่</label>
                  <ThaiDateInput className="form-control-sm" value={checkStart} onChange={v => setCheckStart(v)} />
                </Col>
                <Col xs="auto">
                  <label className="form-label fs-sm mb-1">ถึงวันที่</label>
                  <ThaiDateInput className="form-control-sm" value={checkEnd} onChange={v => setCheckEnd(v)} />
                </Col>
                <Col className="fs-xs text-muted">
                  นับคาบตามตารางสอนของวิชาในกลุ่มสาระ ในวันที่โรงเรียนมีการเช็คชื่อ — ไม่นับวันงดสอนตามปฏิทินและคาบที่ยังไม่ถึงเวลา
                  · เลือกช่วงได้ไม่เกิน 93 วัน
                </Col>
              </Row>
              {checkError ? (
                <div className="text-center text-danger py-4">{checkError}</div>
              ) : !check ? null : (
                <>
                  <Row className="g-2 mb-3">
                    {[
                      { label: 'คาบตามตาราง', value: check.totals.scheduled, color: 'primary' },
                      { label: 'เช็คชื่อแล้ว', value: check.totals.checked, color: 'success' },
                      { label: 'ไม่ได้เช็คชื่อ', value: check.totals.missed, color: 'danger' },
                      { label: 'อัตราการเช็คชื่อ', value: `${pct(check.totals.checked, check.totals.scheduled)}%`, color: 'info' },
                    ].map(k => (
                      <Col key={k.label} xs={6} md={3}>
                        <div className={`border rounded p-2 h-100 border-start border-4 border-${k.color}`}>
                          <div className="fs-xs text-muted">{k.label}</div>
                          <div className={`fs-4 fw-bold text-${k.color}`}>{k.value}</div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                  <Row className="g-3">
                    <Col xl={5}>
                      <h6 className="fs-sm fw-semibold">รายครู</h6>
                      <div style={{ maxHeight: 520, overflowY: 'auto' }}>
                        <Table hover size="sm" className="mb-0">
                          <thead className="table-secondary sticky-top">
                            <tr>
                              <th>ครูผู้สอน</th>
                              <th className="text-center">คาบ</th>
                              <th className="text-center">ไม่ได้เช็ค</th>
                              <th className="text-center">%</th>
                            </tr>
                          </thead>
                          <tbody>
                            {checkTeachers.map(t => (
                              <tr key={t.t_id ?? t.t_name}>
                                <td className="fs-sm">{t.t_name}</td>
                                <td className="text-center">{t.scheduled}</td>
                                <td className={`text-center ${t.missed ? 'text-danger fw-semibold' : 'text-muted'}`}>{t.missed || '-'}</td>
                                <td className="text-center">{pct(t.checked, t.scheduled)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    </Col>
                    <Col xl={7}>
                      <h6 className="fs-sm fw-semibold">คาบที่ไม่ได้เช็คชื่อ <Badge bg="danger">{checkMissed.length}</Badge></h6>
                      {checkMissed.length === 0 ? (
                        <div className="text-center text-muted py-4">ไม่มีคาบที่ไม่ได้เช็คชื่อ</div>
                      ) : (
                        <div style={{ maxHeight: 520, overflowY: 'auto' }}>
                          <Table hover size="sm" className="mb-0">
                            <thead className="table-secondary sticky-top">
                              <tr>
                                <th>วันที่</th>
                                <th className="text-center">คาบ</th>
                                <th className="text-center">ห้อง</th>
                                <th>วิชา</th>
                                <th>ครูผู้สอน</th>
                                <th>เหตุ</th>
                              </tr>
                            </thead>
                            <tbody>
                              {checkMissed.map((m, i) => (
                                <tr key={i} className="fs-sm">
                                  <td className="text-nowrap">{toThaiDate(m.att_date)}</td>
                                  <td className="text-center text-nowrap">{m.period_order} <span className="fs-xs text-muted">({m.period_begin})</span></td>
                                  <td className="text-center text-nowrap">ม.{m.c_id}/{m.r_no}</td>
                                  <td>{m.subject}</td>
                                  <td>{m.t_name}</td>
                                  <td>{m.reason ? <Badge bg="info" className="fs-xs">{m.reason}</Badge> : <span className="text-muted">-</span>}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      )}
                    </Col>
                  </Row>
                </>
              )}
            </Card.Body>
          )}
        </Card>

        <MissingGradesModal row={missingRow} onHide={() => setMissingRow(null)} />

        <Footer />
      </div>
    </React.Fragment>
  );
}
